import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { ChatService } from 'app/modules/admin/chat/chat.service';
import { AuthService } from 'app/core/auth/auth.service';
import { PendingTaskInwardService } from 'app/core/common/services/pending-inward.service';
import * as jwt_decode from 'jwt-decode';

@Component({
    selector     : 'modern-layout',
    templateUrl  : './modern.component.html',
    encapsulation: ViewEncapsulation.None
})
export class ModernLayoutComponent implements OnInit, OnDestroy
{
    isScreenSmall: boolean;
    navigation: Navigation;
    totalUnread = 0;
    loggedInName = '';
    loggedInRole = '';
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _chatService: ChatService,
        private _authService: AuthService,
        private _pendingTaskInwardService: PendingTaskInwardService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Decode the logged-in user's name + role from the JWT for the toolbar.
        try {
            const t = this._authService.accessToken;
            if (t) {
                const d: any = jwt_decode.jwtDecode(t);
                this.loggedInName =
                    d?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname']
                    || d?.given_name || d?.name || d?.unique_name || '';
                this.loggedInRole =
                    d?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
                    || d?.role || '';
            }
        } catch { /* ignore */ }

        // Boot the chat hub once at the layout level (idempotent) so the
        // toolbar badge always reflects unread counts.
        this._chatService.start(() => this._authService.accessToken);
        this._chatService.loadConversations().subscribe();

        this._chatService.conversations$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((list) => {
                this.totalUnread = (list || []).reduce(
                    (s, c) => s + (c.unreadCount || 0), 0
                );
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Open the pending-task popup (was previously auto-opened hourly /
     * on SignalR status update — now opens on click only).
     */
    openPendingTasks(): void
    {
        this._pendingTaskInwardService.openPopup();
    }
}
